/**
 * ChangeRequestDialog
 * ───────────────────
 * Feb 2026 — Phase 3b of B2 IntakeWizard extraction.
 *
 * Pure extraction of the inline dialog at App.js lines ~68266–68357.
 * Same JSX, same POST endpoint, same success/error handling.
 *
 * Used when a user (typically office staff without
 * edit_client_demographics / edit_client_clinical / etc. permission)
 * clicks "Request Change" inside the IntakeWizard for a section they
 * can't edit directly.  Submits to /api/change-requests for biller or
 * admin review.
 */
import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { DebouncedTextarea } from '../ui/debounced-textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangeRequestDialog({
  open,
  onOpenChange,
  // Which section of the client form the user wanted to change
  // (e.g. "demographics", "insurance", "clinical").  Used in the
  // dialog copy and as the POST payload's `field_category`.
  changeRequestSection,
  // Free-text reason — controlled by the parent so the parent can
  // reset it on cancel/success.
  changeRequestReason,
  setChangeRequestReason,
  // The client whose data the change is being requested for.
  editingClient,
  // Optional: parent can re-fetch its change-request list after a
  // successful submit.  Defaults to no-op.
  onSubmitted,
}) {
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!changeRequestReason.trim()) {
      toast.error("Please describe the changes you'd like made");
      return;
    }
    try {
      const payload = {
        client_id: editingClient?.id,
        field_category: changeRequestSection,
        changes: [{
          field_name: changeRequestSection || 'general',
          field_label: changeRequestSection?.replace(/_/g, ' ') || 'General',
          current_value: '',
          proposed_value: changeRequestReason.trim(),
        }],
        reason: changeRequestReason.trim(),
      };
      await axios.post(`${API}/change-requests`, payload);
      toast.success('Change request submitted successfully');
      onOpenChange?.(false);
      setChangeRequestReason?.('');
      onSubmitted?.();
    } catch (error) {
      console.error('Failed to submit change request:', error);
      const errorMsg = error.response?.data?.detail;
      const displayError = typeof errorMsg === 'string'
        ? errorMsg
        : Array.isArray(errorMsg)
          ? errorMsg.map((e2) => e2.msg || e2).join(', ')
          : error.message || 'Unknown error';
      toast.error(`Failed to submit: ${displayError}`);
    }
  };

  const sectionLabel = changeRequestSection?.replace(/_/g, ' ');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid="change-request-dialog"
      >
        <DialogHeader>
          <DialogTitle>Request Client Data Change</DialogTitle>
          <DialogDescription>
            Describe the changes you'd like made to this client's {sectionLabel} information
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
            <p className="text-blue-800">
              <strong>Note:</strong> You don't have permission to edit {sectionLabel} information directly.
              Describe what you'd like changed and a biller or administrator will review your request.
            </p>
          </div>

          <div>
            <Label htmlFor="change-request-text">What changes would you like made? *</Label>
            <DebouncedTextarea
              id="change-request-text"
              name="change-request-text"
              tabIndex={0}
              autoFocus
              className="w-full border border-gray-300 rounded-md p-3 min-h-[120px] focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
              placeholder="Example: Please update the primary insurance to BlueCross BlueShield, Member ID: ABC123456, Group: GRP789..."
              value={changeRequestReason}
              onChange={(e) => setChangeRequestReason?.(e.target.value)}
              data-testid="change-request-reason"
            />
          </div>

          <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
            <p><strong>Client:</strong> {editingClient?.full_name}</p>
            <p><strong>Section:</strong> {sectionLabel}</p>
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange?.(false);
                setChangeRequestReason?.('');
              }}
              data-testid="change-request-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700"
              disabled={!changeRequestReason.trim()}
              data-testid="change-request-submit"
            >
              Submit Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
